import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLessons, createLesson } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Plus, 
  Search,
  RefreshCw,
  Lightbulb,
  Tag,
  Filter,
  ThumbsUp,
  MessageSquare,
  Calendar,
  Zap,
  CheckCircle,
  TrendingUp,
  Award,
  ChevronsUpDown,
  Check,
  X,
  Share2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const LessonsPage = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [helpfulLessons, setHelpfulLessons] = useState({});
  const [viewingLesson, setViewingLesson] = useState(null);
  
  // Custom entries state
  const [customProjectTypes, setCustomProjectTypes] = useState([]);
  const [customAssetTypes, setCustomAssetTypes] = useState([]);
  const [newProjectType, setNewProjectType] = useState('');
  const [newAssetType, setNewAssetType] = useState('');
  const [showAddProjectType, setShowAddProjectType] = useState(false);
  const [showAddAssetType, setShowAddAssetType] = useState(false);
  
  // Default options
  const defaultProjectTypes = ['Equipment Replacement', 'Testing', 'Emergency', 'Maintenance', 'New Installation'];
  const defaultAssetTypes = ['Transformer', 'Circuit Breaker', 'Disconnect Switch', 'Protection Relays', 'Bus Work', 'Control Building', 'Battery System'];
  
  const [newLesson, setNewLesson] = useState({
    title: '',
    content: '',
    tags: '',
    project_type: '',
    asset_type: '',
    outcome: ''
  });

  useEffect(() => {
    fetchLessons();
    // Load custom types from localStorage
    const savedProjectTypes = localStorage.getItem('customProjectTypes');
    const savedAssetTypes = localStorage.getItem('customAssetTypes');
    if (savedProjectTypes) setCustomProjectTypes(JSON.parse(savedProjectTypes));
    if (savedAssetTypes) setCustomAssetTypes(JSON.parse(savedAssetTypes));
    // Load helpful lessons from localStorage
    const savedHelpful = localStorage.getItem('helpfulLessons');
    if (savedHelpful) setHelpfulLessons(JSON.parse(savedHelpful));
  }, [projectTypeFilter, assetTypeFilter]);

  const fetchLessons = async () => {
    setLoading(true);
    try {
      const params = {};
      if (projectTypeFilter !== 'all') params.project_type = projectTypeFilter;
      if (assetTypeFilter !== 'all') params.asset_type = assetTypeFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await getLessons(params);
      setLessons(response.data);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLessons();
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    if (!newLesson.title || !newLesson.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await createLesson({
        ...newLesson,
        tags: newLesson.tags ? newLesson.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('Lesson shared successfully!');
      setShowCreateDialog(false);
      setNewLesson({
        title: '',
        content: '',
        tags: '',
        project_type: '',
        asset_type: '',
        outcome: ''
      });
      fetchLessons();
    } catch (error) {
      toast.error('Failed to create lesson');
    }
  };

  const handleHelpful = (lessonId) => {
    setHelpfulLessons(prev => {
      const newState = {
        ...prev,
        [lessonId]: !prev[lessonId]
      };
      localStorage.setItem('helpfulLessons', JSON.stringify(newState));
      if (!prev[lessonId]) {
        toast.success('Thanks for your feedback!');
      }
      return newState;
    });
  };

  const handleAddProjectType = () => {
    if (!newProjectType.trim()) return;
    const updated = [...customProjectTypes, newProjectType.trim()];
    setCustomProjectTypes(updated);
    localStorage.setItem('customProjectTypes', JSON.stringify(updated));
    setNewLesson({ ...newLesson, project_type: newProjectType.trim() });
    setNewProjectType('');
    setShowAddProjectType(false);
    toast.success('Project type added!');
  };

  const handleAddAssetType = () => {
    if (!newAssetType.trim()) return;
    const updated = [...customAssetTypes, newAssetType.trim()];
    setCustomAssetTypes(updated);
    localStorage.setItem('customAssetTypes', JSON.stringify(updated));
    setNewLesson({ ...newLesson, asset_type: newAssetType.trim() });
    setNewAssetType('');
    setShowAddAssetType(false);
    toast.success('Asset type added!');
  };

  const allProjectTypes = [...defaultProjectTypes, ...customProjectTypes];
  const allAssetTypes = [...defaultAssetTypes, ...customAssetTypes];

  const getProjectTypeIcon = (type) => {
    switch (type) {
      case 'Equipment Replacement': return <Zap className="w-4 h-4" />;
      case 'Testing': return <CheckCircle className="w-4 h-4" />;
      case 'Emergency': return <TrendingUp className="w-4 h-4" />;
      default: return <Lightbulb className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (type) => {
    switch (type) {
      case 'Equipment Replacement': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Testing': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Emergency': return 'bg-red-100 text-red-800 border-red-200';
      case 'Maintenance': return 'bg-green-100 text-green-800 border-green-200';
      case 'New Installation': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const stats = {
    total: lessons.length,
    thisMonth: lessons.filter(l => {
      const date = new Date(l.created_at);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    byType: lessons.reduce((acc, l) => {
      const type = l.project_type || 'Other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {})
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="lessons-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            Lessons Learned
          </h1>
          <p className="text-slate-600 mt-1">Knowledge from past projects to help future success</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700" data-testid="create-lesson-btn">
              <Plus className="w-4 h-4 mr-2" />
              Share Knowledge
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 border-green-200">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-800">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                Share a Lesson Learned
              </DialogTitle>
              <DialogDescription className="text-green-700">
                Help your team by sharing knowledge from your experience
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLesson} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-green-800 font-medium">Title *</Label>
                <Input
                  placeholder="What did you learn?"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                  required
                  className="bg-white/80 border-green-200 focus:border-green-400"
                  data-testid="lesson-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-green-800 font-medium">Description *</Label>
                <Textarea
                  placeholder="Describe the situation, what happened, and what was learned..."
                  value={newLesson.content}
                  onChange={(e) => setNewLesson({ ...newLesson, content: e.target.value })}
                  rows={4}
                  required
                  className="bg-white/80 border-green-200 focus:border-green-400"
                  data-testid="lesson-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-green-800 font-medium">Project Type</Label>
                  <Popover open={showAddProjectType} onOpenChange={setShowAddProjectType}>
                    <div className="flex gap-1">
                      <Select 
                        value={newLesson.project_type} 
                        onValueChange={(v) => setNewLesson({ ...newLesson, project_type: v })}
                      >
                        <SelectTrigger className="flex-1 bg-white/80 border-green-200">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {allProjectTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="icon" className="border-green-200 hover:bg-green-100">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                    </div>
                    <PopoverContent className="w-64 p-3" align="end">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Add New Project Type</Label>
                        <Input
                          placeholder="e.g., Inspection"
                          value={newProjectType}
                          onChange={(e) => setNewProjectType(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProjectType())}
                        />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => setShowAddProjectType(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button type="button" size="sm" onClick={handleAddProjectType} className="flex-1 bg-green-600 hover:bg-green-700">
                            Add
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-green-800 font-medium">Asset Type</Label>
                  <Popover open={showAddAssetType} onOpenChange={setShowAddAssetType}>
                    <div className="flex gap-1">
                      <Select 
                        value={newLesson.asset_type} 
                        onValueChange={(v) => setNewLesson({ ...newLesson, asset_type: v })}
                      >
                        <SelectTrigger className="flex-1 bg-white/80 border-green-200">
                          <SelectValue placeholder="Select asset" />
                        </SelectTrigger>
                        <SelectContent>
                          {allAssetTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="icon" className="border-green-200 hover:bg-green-100">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                    </div>
                    <PopoverContent className="w-64 p-3" align="end">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Add New Asset Type</Label>
                        <Input
                          placeholder="e.g., Capacitor Bank"
                          value={newAssetType}
                          onChange={(e) => setNewAssetType(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAssetType())}
                        />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => setShowAddAssetType(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button type="button" size="sm" onClick={handleAddAssetType} className="flex-1 bg-green-600 hover:bg-green-700">
                            Add
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-green-800 font-medium">Tags (comma separated)</Label>
                <Input
                  placeholder="safety, best-practice, oil-testing"
                  value={newLesson.tags}
                  onChange={(e) => setNewLesson({ ...newLesson, tags: e.target.value })}
                  className="bg-white/80 border-green-200 focus:border-green-400"
                  data-testid="lesson-tags-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-green-800 font-medium">Outcome / Result</Label>
                <Input
                  placeholder="What was the positive result of applying this lesson?"
                  value={newLesson.outcome}
                  onChange={(e) => setNewLesson({ ...newLesson, outcome: e.target.value })}
                  className="bg-white/80 border-green-200 focus:border-green-400"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-green-300 hover:bg-green-100">
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="submit-lesson-btn">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Lesson
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setProjectTypeFilter('all'); setAssetTypeFilter('all'); }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Total Lessons</p>
                <p className="text-3xl font-bold text-green-800">{stats.total}</p>
              </div>
              <BookOpen className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">This Month</p>
                <p className="text-3xl font-bold text-blue-800">{stats.thisMonth}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Categories</p>
                <p className="text-3xl font-bold text-purple-800">{Object.keys(stats.byType).length}</p>
              </div>
              <Filter className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Contributors</p>
                <p className="text-3xl font-bold text-amber-800">
                  {new Set(lessons.map(l => l.created_by)).size}
                </p>
              </div>
              <Award className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search lessons by title or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-lessons-input"
              />
            </div>
            <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Project Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Project Types</SelectItem>
                {allProjectTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Asset Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                {allAssetTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#0056D2]" data-testid="search-lessons-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Lessons List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
        </div>
      ) : lessons.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lightbulb className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No Lessons Found</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              {searchQuery || projectTypeFilter !== 'all' || assetTypeFilter !== 'all'
                ? 'Try adjusting your search criteria to find more lessons.'
                : 'Be the first to share knowledge from your project experience!'}
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Share First Lesson
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {lessons.map((lesson) => (
            <Card 
              key={lesson.id}
              className="overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`lesson-card-${lesson.id}`}
            >
              <div className={`h-2 ${lesson.project_type === 'Equipment Replacement' ? 'bg-blue-500' : lesson.project_type === 'Testing' ? 'bg-purple-500' : lesson.project_type === 'Emergency' ? 'bg-red-500' : 'bg-green-500'}`} />
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {lesson.project_type && (
                        <Badge className={getCategoryColor(lesson.project_type)}>
                          {getProjectTypeIcon(lesson.project_type)}
                          <span className="ml-1">{lesson.project_type}</span>
                        </Badge>
                      )}
                      {lesson.asset_type && (
                        <Badge variant="outline">{lesson.asset_type}</Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{lesson.title}</h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{lesson.content}</p>
                    
                    {lesson.outcome && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-700">OUTCOME</span>
                        </div>
                        <p className="text-sm text-green-800">{lesson.outcome}</p>
                      </div>
                    )}

                    {lesson.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {lesson.tags.map((tag, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 bg-slate-100 rounded-full text-xs text-slate-600 flex items-center gap-1"
                          >
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-[#0056D2] text-white text-xs">
                            {lesson.created_by_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{lesson.created_by_name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(lesson.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setViewingLesson(lesson)}
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleHelpful(lesson.id)}
                          className={`${helpfulLessons[lesson.id] ? 'text-green-600 bg-green-50' : 'text-slate-500 hover:text-green-600 hover:bg-green-50'}`}
                        >
                          <ThumbsUp className={`w-4 h-4 mr-1 ${helpfulLessons[lesson.id] ? 'fill-current' : ''}`} />
                          {helpfulLessons[lesson.id] ? 'Helpful!' : 'Helpful'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Lesson Dialog */}
      <Dialog open={!!viewingLesson} onOpenChange={() => setViewingLesson(null)}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 border-green-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-green-800">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              {viewingLesson?.title}
            </DialogTitle>
            <DialogDescription className="text-green-700">
              Shared by {viewingLesson?.created_by_name} on {viewingLesson && new Date(viewingLesson.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          {viewingLesson && (
            <div className="space-y-4 pt-2">
              <div className="flex flex-wrap gap-2">
                {viewingLesson.project_type && (
                  <Badge className={getCategoryColor(viewingLesson.project_type)}>
                    {getProjectTypeIcon(viewingLesson.project_type)}
                    <span className="ml-1">{viewingLesson.project_type}</span>
                  </Badge>
                )}
                {viewingLesson.asset_type && (
                  <Badge variant="outline" className="border-green-300">{viewingLesson.asset_type}</Badge>
                )}
              </div>
              
              <div className="prose prose-sm max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">{viewingLesson.content}</p>
              </div>

              {viewingLesson.outcome && (
                <div className="p-4 bg-white/60 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">Outcome</span>
                  </div>
                  <p className="text-green-800">{viewingLesson.outcome}</p>
                </div>
              )}

              {viewingLesson.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {viewingLesson.tags.map((tag, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 bg-white/60 rounded-full text-sm text-green-700 flex items-center gap-1 border border-green-200"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-green-200">
                <div className="flex items-center gap-2">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-green-600 text-white">
                      {viewingLesson.created_by_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-green-800">{viewingLesson.created_by_name}</p>
                    <p className="text-sm text-green-600">Contributor</p>
                  </div>
                </div>
                <Button 
                  onClick={() => { handleHelpful(viewingLesson.id); setViewingLesson(null); }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <ThumbsUp className={`w-4 h-4 mr-2 ${helpfulLessons[viewingLesson.id] ? 'fill-current' : ''}`} />
                  {helpfulLessons[viewingLesson.id] ? 'Marked as Helpful!' : 'Mark as Helpful'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LessonsPage;
